import { notification } from 'antd';
import React, { useEffect } from 'react';
import { useAsync } from 'react-async';
import { LoaderFullPage } from '../components/common';
import { UserModel } from '../model/UserModel';
import * as authClient from '../utils/auth-client';
import { bootstrapAppData, brodcastUserChange, getUser } from '../utils/auth-client';

export interface IAuthContext {
  data: { user: UserModel; },
  login: any,
  logout: any,
  register: any,
  reload: any,
  changeContext: any,
}

let authRefreshTimeout = 60 * 1000; //60s

export var AuthContext: React.Context<IAuthContext> = React.createContext({
  data:          { user: new UserModel() },
  login:         null,
  logout:        null,
  register:      null,
  reload:        null,
  changeContext: null,
});

export const AuthProvider = (props: any) => {
  const [intervalPointer, setIntervalPointer] = React.useState<NodeJS.Timeout>();
  const [firstAttemptFinished, setFirstAttemptFinished] = React.useState(false);
  const {
          data = { user: {} },
          error,
          isRejected,
          isPending,
          isSettled,
          reload,
        } = useAsync({
    promiseFn: bootstrapAppData,
  });

  React.useLayoutEffect(() => {
    if (isSettled) {
      setFirstAttemptFinished(true);
    }
  }, [isSettled]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const logout = () => {
    if (intervalPointer) {
      clearInterval(intervalPointer);
      setIntervalPointer(undefined);
    }
    brodcastUserChange();
    authClient.logout(true);
    // eslint-disable-next-line no-restricted-globals
    window.location.href = '/';
    return reload();
  };

  useEffect((): any => {
    if (!intervalPointer) {
      setIntervalPointer(setInterval(() => {
        getUser()
          .catch((e) => {
            //e means network/etc error. If e is null means that we dont have user response/401 etc
            if (e && logout) {
              logout();
            }
          });
      }, authRefreshTimeout));
    }
    return () => {
      if (intervalPointer) {
        clearTimeout(intervalPointer);
        setIntervalPointer(undefined);
      }
    };
  }, [intervalPointer, logout]);

  if (error) {
    notification.error({
      message:     'Request error.',
      description: '<>error<>',
    });
    return <LoaderFullPage />;
  }

  if (!firstAttemptFinished) {
    if (isPending) {
      return <LoaderFullPage />;
    }
    if (isRejected) {
      notification.error({
        message:     'Request error.',
        description: error,
      });
      return <LoaderFullPage />;
    }
  }

  const login = (form: any) => authClient.login(form)
                                         .then(() => {
                                           reload();
                                         });
  const register = (form: any) => authClient.register(form);
  const changeContext = (id: string) => authClient.changeContext(id)
                                                  .then((resp) => {
                                                      reload();
                                                      return resp;
                                                    },
                                                  );

  return (
    <AuthContext.Provider value={{
      data,
      login,
      logout,
      register,
      reload,
      changeContext,
    }} {...props} />
  );
};

export let useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
};
